import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2, Search, X } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { listJournalOrders, type OrderCard } from "@/api/order_journal";
import { DateInput } from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { checkStatus, kindTitle } from "@/lib/order-journal";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { useDebounced } from "@/lib/use-debounced";
import { StatusBadge } from "@/pages/order-journal/parts";

/**
 * Журнал отгрузки — список заказов, по которым была проверка (сборка).
 *
 * Read-only, данные целиком из базы портала. Поиск — подстрока номера заказа
 * или контрагента, период — по ДАТЕ ЗАКАЗА (претензию клиент присылает по
 * заказу), сортировка на сервере — последняя активность сверху. Пагинация —
 * кнопкой «Показать ещё»: страница журнала открывается и на телефоне, и
 * бесконечная лента там прячет низ списка.
 */
export function OrderJournalListPage(): React.ReactElement {
  const [rawQuery, setRawQuery] = useState("");
  const query = useDebounced(rawQuery.trim(), 350);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fromErr, setFromErr] = useState("");
  const [toErr, setToErr] = useState("");
  const datesBad = Boolean(fromErr || toErr) || Boolean(dateFrom && dateTo && dateFrom > dateTo);

  const listQ = useInfiniteQuery({
    queryKey: ["order-journal-orders", query, dateFrom, dateTo],
    queryFn: ({ pageParam }) =>
      listJournalOrders({ q: query, date_from: dateFrom, date_to: dateTo, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) =>
      last.has_more ? all.reduce((n, p) => n + p.items.length, 0) : undefined,
    // Неверная дата не уходит на сервер как «без даты»: список ждёт исправления.
    enabled: !datesBad,
  });
  const rows = listQ.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-3">
      <h1 className="text-xl font-semibold">Журнал отгрузки</h1>

      <Card className="flex flex-col gap-3 p-3 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="block min-w-0 flex-1 sm:min-w-64">
          <span className="mb-1 block text-xs text-muted-foreground">Заказ или контрагент</span>
          <span className="relative block">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              placeholder="Номер заказа или название контрагента"
              className="pl-8 pr-8"
              aria-label="Поиск по номеру заказа или контрагенту"
            />
            {rawQuery && (
              <button
                type="button"
                onClick={() => setRawQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                aria-label="Очистить поиск"
              >
                <X className="size-4" />
              </button>
            )}
          </span>
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <span className="mb-1 block text-xs text-muted-foreground">Дата заказа с</span>
            <DateInput
              value={dateFrom}
              ariaLabel="Дата заказа с"
              className="w-44"
              onChange={(iso, err) => {
                setFromErr(err);
                if (!err) setDateFrom(iso);
              }}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs text-muted-foreground">по</span>
            <DateInput
              value={dateTo}
              ariaLabel="Дата заказа по"
              className="w-44"
              onChange={(iso, err) => {
                setToErr(err);
                if (!err) setDateTo(iso);
              }}
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setFromErr("");
                setToErr("");
              }}
            >
              Сбросить даты
            </Button>
          )}
        </div>
      </Card>

      {datesBad && !fromErr && !toErr && (
        <p className="text-sm text-rose-700">Начало периода позже его конца — исправьте даты.</p>
      )}

      {listQ.isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {listQ.isError && (
        <Card role="alert" className="border-destructive/50 p-3 text-sm text-destructive">
          Не удалось загрузить журнал: {apiErrorText(listQ.error)}
        </Card>
      )}
      {listQ.isSuccess && rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {query ? `По запросу «${query}» заказов нет` : "Заказов с проверкой за этот период нет"}
        </p>
      )}

      {rows.length > 0 && (
        <Card className="divide-y overflow-hidden">
          <div className="hidden gap-3 px-3 py-2 text-xs text-muted-foreground lg:flex">
            <span className="w-36 shrink-0">Заказ</span>
            <span className="min-w-0 flex-1">Контрагент</span>
            <span className="w-28 shrink-0">Вид</span>
            <span className="w-40 shrink-0">Проверка</span>
            <span className="w-32 shrink-0 text-right">Паллет отгружено</span>
            <span className="w-32 shrink-0 text-right">Расходная</span>
          </div>
          {rows.map((r) => (
            <OrderRow key={r.order_ref} row={r} />
          ))}
        </Card>
      )}

      {listQ.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            disabled={listQ.isFetchingNextPage}
            onClick={() => void listQ.fetchNextPage()}
          >
            {listQ.isFetchingNextPage && <Loader2 className="size-4 animate-spin" />}
            Показать ещё
          </Button>
        </div>
      )}
    </div>
  );
}

function OrderRow({ row }: { row: OrderCard }): React.ReactElement {
  const st = checkStatus(row.status);
  return (
    <Link
      to={`/order-journal/${row.order_ref}`}
      data-testid="journal-order-row"
      className="flex flex-col gap-1 px-3 py-2 text-sm transition-colors hover:bg-accent lg:flex-row lg:items-center lg:gap-3"
    >
      <span className="lg:w-36 lg:shrink-0">
        <span className="font-semibold">{row.order_number}</span>
        {row.order_date && (
          <span className="ml-2 text-xs text-muted-foreground lg:ml-0 lg:block">
            от {fmtDate(row.order_date)}
          </span>
        )}
      </span>
      <span className="min-w-0 break-words lg:flex-1">
        {row.kontragent_name || "—"}
        {row.organization_name && (
          <span className="block text-xs text-muted-foreground">{row.organization_name}</span>
        )}
      </span>
      <span className="text-xs text-muted-foreground lg:w-28 lg:shrink-0">
        {kindTitle(row.kind)}
      </span>
      <span className="lg:w-40 lg:shrink-0">
        <StatusBadge tone={st.tone}>{st.text}</StatusBadge>
        <span className="ml-2 text-xs text-muted-foreground lg:ml-0 lg:block">
          {row.sessions_count > 1 ? `проверок: ${row.sessions_count} · ` : ""}
          {fmtDateTime(row.last_activity_at)}
        </span>
      </span>
      <span className="text-xs lg:w-32 lg:shrink-0 lg:text-right lg:text-sm">
        <span className="lg:hidden text-muted-foreground">Паллет отгружено: </span>
        <span className="font-medium tabular-nums">
          {row.pallets_shipped} из {row.pallets_total}
        </span>
        {row.pallets_shipped_external > 0 && (
          <span className="block text-xs text-amber-700">
            ещё {row.pallets_shipped_external} вне портала
          </span>
        )}
        {row.without_unit > 0 && (
          <span className="block text-xs text-red-700">без грузоместа: {row.without_unit}</span>
        )}
      </span>
      <span className="text-xs lg:w-32 lg:shrink-0 lg:text-right">
        {row.expense_number ? `Расходная № ${row.expense_number}` : "Расходной нет"}
      </span>
    </Link>
  );
}
